import getFiltersFromGrid from '@/features/filters/helpers/getFiltersFromGrid';
import { IChoiceFilters } from '@/interfaces/choice.interfaces';
import { FilterRootGridType } from '@/interfaces/constructor.interfaces';
import isDefined from '@/utils/isDefined';

/**
 * Вспомогательная функция. Рекурсивно проходится по фильтрам
 * и собирает набор ключей фильтров, значения которых нужно сбросить.
 * @param key ключ фильтра
 * @param filters все фильтры
 * @returns массив ключей, которые будут сброшены.
 */
export function getFilterKeysToReset(
  key: string,
  filters: { key: string; resetOnChange?: string[] }[],
): Array<string> {
  const filterKeys: Array<string> = [];
  filters.forEach(filter => {
    const isReset = filter.resetOnChange?.includes(key);

    if (!isReset) {
      return;
    }

    const subDeps = getFilterKeysToReset(filter.key, filters);
    filterKeys.push(filter.key, ...subDeps);
  });

  return filterKeys;
}

/**
 * Возвращает список фильтров, значения которых нужно сбросить,
 * если изменился фильтр, переданный параметром id.
 * Данная функция учитывает зависимости зависимостей, для
 * того, чтобы их сбросить разом.
 * Например, если меняется фильтр "Модель".
 * То от него может завесить фильтр "Комплектация", он должен быть сброшен.
 * От комплектации зависит "Двигатель", поэтому он так же должен быть сброшен.
 * @param key ключ фильтра
 * @param grid грид, фильтров с их настройками
 * @param choice текущие, выбранные значения
 * @returns массив ключей фильтров, которые нужно сбросить.
 */
export const getDependantFiltersToReset = (
  key: string,
  grid: FilterRootGridType,
  choice: IChoiceFilters,
): Array<string> => {
  const filters = getFiltersFromGrid(grid);

  // Находим массив фильтров, которые зависят от переданного
  const filtersToReset = getFilterKeysToReset(key, filters);
  // Исключаем дубликаты
  const uniqueFiltersSet = new Set(filtersToReset);
  const uniqueFilters = Array.from(uniqueFiltersSet.values());
  // Исключаем из сброса фильтры, у которых значения не проставлены.
  const nonEmptyFiltersToReset = uniqueFilters.filter(filterKey =>
    isDefined(choice[filterKey]),
  );

  return nonEmptyFiltersToReset;
};

export default getDependantFiltersToReset;
